import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { callAPI } from "../../helper/apiUtils";

const REFRESH_MIN_INTERVAL_MS = 20 * 1000;
const CHATS_LIMIT = 6;
const EMAILS_LIMIT = 3;
const REPORTS_LIMIT = 30;

function isTrashed(em) {
  const labels = Array.isArray(em && em.labels) ? em.labels : [];
  return labels.some((l) => String(l).toUpperCase() === "TRASH");
}

function extractEmailMessages(data) {
  if (!data) return [];
  if (Array.isArray(data.messages)) return data.messages;
  if (Array.isArray(data.emails)) return data.emails;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.data?.messages)) return data.data.messages;
  return [];
}

/**
 * Loads everything the cockpit shows:
 *   - GET /user/cockpit          (todo KPIs, lists, rooms, daily report, email, company)
 *   - GET /group/getUserChats    (latest chats, only when chats are enabled)
 *   - GET /email/messages        (latest inbox rows, io + email client mode only)
 *   - GET /email/messages?folder=BRIEFING (daily reports — the cockpit is the
 *                                  only place they are listed)
 * plus the live unread totals from Redux (`unSeenAllCount`, socket driven).
 *
 * Refreshes when the pane becomes active again, on window focus, whenever
 * the badge totals change (throttled), when mail arrives or leaves (socket),
 * and after a socket reconnect catch-up.
 */
export default function useCockpitData({ active, withChats, withEmails, withReports }) {
  const unSeenAllCount = useSelector((s) => s.unSeenAllCount);
  const unseenTodoCount = useSelector((s) => s.unseenTodoCount);
  const language = useSelector((s) => s.language);
  const emailSyncEvents = useSelector((s) => s.emailSyncEvents);
  const emailCatchUpAt = useSelector((s) => s.emailCatchUpAt);

  const [overview, setOverview] = useState(null);
  const [chats, setChats] = useState([]);
  const [emails, setEmails] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const inFlightRef = useRef(false);
  const lastLoadRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ silent = false, force = false } = {}) => {
      if (inFlightRef.current) return;
      if (!force && Date.now() - lastLoadRef.current < REFRESH_MIN_INTERVAL_MS) return;
      inFlightRef.current = true;
      if (!silent) setLoading(true);
      try {
        const lang = String(language || "").split("-")[0];
        const langParam = lang ? `&translationLang=${encodeURIComponent(lang)}` : "";
        const [overviewRes, chatsRes, emailsRes, reportsRes] = await Promise.all([
          callAPI("/user/cockpit", {}, "GET"),
          withChats
            ? callAPI(`/group/getUserChats?page=1&limit=${CHATS_LIMIT}${langParam}`, {}, "GET").catch(() => null)
            : Promise.resolve(null),
          withEmails
            ? callAPI(`/email/messages?folder=INBOX&page=1&limit=${EMAILS_LIMIT}`, {}, "GET").catch(() => null)
            : Promise.resolve(null),
          withReports
            ? callAPI(`/email/messages?folder=BRIEFING&page=1&limit=${REPORTS_LIMIT}`, {}, "GET").catch(() => null)
            : Promise.resolve(null),
        ]);
        if (!mountedRef.current) return;
        const overviewData = overviewRes?.data;
        if (overviewData?.status && overviewData.data) {
          setOverview(overviewData.data);
          setError(null);
        } else if (!overview) {
          setError(overviewData?.message || "unknown");
        }
        if (withChats) {
          const rows = chatsRes?.data?.chats;
          setChats(Array.isArray(rows) ? rows.filter((c) => c && !c.isSelfChat).slice(0, CHATS_LIMIT) : []);
        } else {
          setChats([]);
        }
        if (withEmails) {
          setEmails(
            extractEmailMessages(emailsRes?.data)
              .filter((m) => m && String(m.systemTag || "") !== "daily_briefing")
              .slice(0, EMAILS_LIMIT)
          );
        } else {
          setEmails([]);
        }
        if (withReports) {
          // Keep the previous list on a failed fetch (null) instead of blanking the card.
          if (reportsRes) {
            setReports(extractEmailMessages(reportsRes?.data).filter((m) => m && !isTrashed(m)));
          }
        } else {
          setReports([]);
        }
        lastLoadRef.current = Date.now();
        setLastLoadedAt(new Date());
      } catch (e) {
        if (mountedRef.current && !overview) setError(e?.message || "unknown");
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [withChats, withEmails, withReports, language]
  );

  // Initial load + whenever the pane becomes active again.
  useEffect(() => {
    if (!active) return;
    load({ force: lastLoadRef.current === 0 });
  }, [active, load]);

  // Chats / emails / reports toggles changed -> reload those sections.
  useEffect(() => {
    if (!active || lastLoadRef.current === 0) return;
    load({ silent: true, force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withChats, withEmails, withReports]);

  // Window focus -> quiet refresh.
  useEffect(() => {
    if (!active) return undefined;
    const onFocus = () => load({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [active, load]);

  // Badge totals change (socket) -> quiet refresh (throttled by load()).
  const badgeKey = JSON.stringify([
    unSeenAllCount?.unseenChatCount,
    unSeenAllCount?.unseenEmailCount,
    unSeenAllCount?.unseenExternalProject,
    unSeenAllCount?.unseenInternalProject,
    unSeenAllCount?.unseenAllScopeDailyBriefingCount,
    unseenTodoCount,
  ]);
  useEffect(() => {
    if (!active || lastLoadRef.current === 0) return;
    load({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badgeKey]);

  // Mail arrived or left (socket), or a catch-up after a reconnect -> quiet
  // refresh. Not throttled like the badge path: mail that lands right after
  // a load must still show up, and a read mail does not move the badge.
  // Debounced so a burst loads once; waits for a load already in flight.
  let mailChangeSeq = 0;
  for (const e of emailSyncEvents || []) {
    if (e.action === "increase" || e.action === "decrease" || e.action === "delete") mailChangeSeq = e.__seq;
  }
  useEffect(() => {
    if (!active || !withEmails || lastLoadRef.current === 0) return undefined;
    if (!mailChangeSeq && !emailCatchUpAt) return undefined;
    let timer = null;
    const run = () => {
      if (inFlightRef.current) {
        timer = setTimeout(run, 1500);
        return;
      }
      load({ silent: true, force: true });
    };
    timer = setTimeout(run, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailChangeSeq, emailCatchUpAt]);

  return {
    overview,
    chats,
    emails,
    reports,
    loading,
    error,
    lastLoadedAt,
    refresh: () => load({ silent: true, force: true }),
    setOverview,
    setReports,
  };
}
